import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";
import { ResponsivePicture, TAN_STANDING_SEAM_PINE_RIDGE } from "@/components/site/images";
import {
  HERNANDO_GALVALUME_FINISHED,
  HOMOSASSA_BLUE_METAL_IN_PROGRESS,
  LECANTO_METAL_BEFORE,
  LECANTO_METAL_AFTER,
  LECANTO_STANDING_SEAM_GALVALUME,
} from "@/components/site/project-images";
import { Phone, CalendarCheck } from "lucide-react";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Is standing seam better than exposed-fastener metal?",
    a: "Standing seam is a more premium concealed-fastener system, but it costs substantially more. Exposed-fastener metal is often the practical choice when a homeowner wants metal roofing at a lower initial price. The best system depends on the roof, budget, appearance, and long-term priorities.",
  },
  {
    q: "What metal gauge does Destination Roofing use?",
    a: "Destination Roofing generally uses 26-gauge or heavier metal for residential exposed-fastener systems and approximately 24-gauge metal for standing seam, depending on the panel system, engineering, manufacturer, and project requirements.",
  },
  {
    q: "What is installed beneath a metal roof?",
    a: "Destination Roofing installs high-temperature peel-and-stick underlayment beneath metal roofing. The exact product may vary based on compatibility, availability, and project requirements.",
  },
  {
    q: "Can a leaking metal roof be repaired?",
    a: "Yes, when the problem is isolated and the surrounding panels remain structurally sound. Failed screws, washers, sealant, trim, and penetrations may be repairable.",
  },
  {
    q: "What is a complete metal-roof re-screw?",
    a: "The existing screws are removed and replaced with wider-diameter long-life ZAC screws that can properly engage the existing fastener holes. Penetrations and vulnerable details are resealed as needed.",
  },
  {
    q: "When is a re-screw not recommended?",
    a: "A re-screw is usually not recommended when panels are widely rusted, severely deteriorated, damaged beyond repair, or unable to hold replacement fasteners reliably.",
  },
  {
    q: "Does a metal roof require maintenance?",
    a: "Yes. Exposed-fastener roofs should be inspected over time for backing-out screws, washer deterioration, failed sealant, damaged trim, and penetration leaks. Standing-seam systems also require proper inspection and maintenance.",
  },
  {
    q: "Can metal be installed over shingles?",
    a: "Sometimes, but the decision depends on existing layers, deck condition, code, manufacturer requirements, ventilation, moisture conditions, and the approved installation method. Tear-off allows the roof deck to be fully inspected.",
  },
  {
    q: "How much more does standing seam cost?",
    a: "Standing seam is substantially more expensive than exposed-fastener metal because of the material, fabrication, concealed-fastener system, and installation requirements. A reliable comparison requires measuring and inspecting the actual roof.",
  },
  {
    q: "Which exposed-fastener profile do you install most often?",
    a: "Tough Rib or Ultra Rib is the most common exposed-fastener profile installed by Destination Roofing, although 5V Crimp and PBR panels may be used where appropriate.",
  },
  {
    q: "Which metal suppliers do you use?",
    a: "Destination Roofing works with suppliers including Tri County Metals, Premier Metals, and Metal Ops. The selected supplier and product depend on the project, availability, specifications, and homeowner selection.",
  },
  {
    q: "Does metal roofing help during Florida storms?",
    a: "A properly designed and installed metal system can provide strong performance, but no roof is storm-proof. Panel system, fastening, edge details, flashing, roof shape, substrate, and installation quality all influence performance.",
  },
  {
    q: "What workmanship warranty comes with a new metal roof?",
    a: "Standard full-roof installations generally include a 15-year Destination Roofing workmanship warranty. Exact coverage is controlled by the written contract and warranty.",
  },
  {
    q: "Who performs the inspection?",
    a: "Owner and licensed roofing contractor Deston Judy generally performs metal-roof inspections personally and explains whether repair, re-screwing, or replacement appears most reasonable.",
  },
];

const REVIEWS = [
  {
    name: "Travis (Local Guide)",
    text: "Thanks for your help and knowledge in my roof situation. He came out on a holiday to fix my leak, it was an easy fix he didn't even charge me and that says enough about this business. Thank you! Highly recommend.",
  },
  {
    name: "Andrea Smith",
    text: "Destination was one of three roofing companies called for a small roof leak. First guy said we needed plywood etc. Destin was honest and fixed the actual problem.",
  },
  {
    name: "John Pollette",
    text: "Destin came out after other roofing companies came out and found the leak that no one else could find. He is super professional.",
  },
];

export const Route = createFileRoute("/metal-roofing")({
  head: () => ({
    meta: [
      { title: "Metal Roofing in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "Metal roofing in Citrus County, FL, including exposed-fastener, standing seam, repairs, and re-screws. Free owner-performed inspection. Call (352) 901-3094." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Metal Roofing in Citrus County, FL | Destination Roofing" },
      { property: "og:description", content: "Metal roofing in Citrus County, FL, including exposed-fastener, standing seam, repairs, and re-screws. Free owner-performed inspection." },
      { property: "og:url", content: canonical("/metal-roofing") },
      { property: "og:type", content: "website" },
      { property: "og:image", content: TAN_STANDING_SEAM_PINE_RIDGE.fallback },
    ],
    links: [{ rel: "canonical", href: canonical("/metal-roofing") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "Services", href: "/services" }, { label: "Metal Roofing", href: "/metal-roofing" }]),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Metal Roofing",
          serviceType: "Metal Roofing",
          description: "Exposed-fastener and standing-seam metal roof installation, replacement, repair, and complete re-screw service in Citrus County, FL.",
          url: canonical("/metal-roofing"),
          provider: { "@id": ROOFING_CONTRACTOR_ID },
          areaServed: { "@type": "AdministrativeArea", name: "Citrus County, Florida" },
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ]),
    }],
  }),
  component: Page,
});

function TrustRow() {
  const items = [
    "Free inspections",
    "Owner-performed inspections",
    "Exposed-fastener and standing-seam systems",
    "Metal-roof repairs and re-screws",
    "Licensed and insured",
    `License #${" "}${BUSINESS.licenseNumber}`,
  ];
  return (
    <div className="not-prose bg-muted/50 border border-border rounded-lg p-4 my-6">
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {items.map((t) => (
          <li key={t} className="flex items-start gap-2">
            <span className="mt-1 size-1.5 rounded-full bg-cta shrink-0" aria-hidden />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WhyCards() {
  const cards = [
    { h: "Owner Involvement", p: "Deston generally inspects the roof, explains the estimate, checks the completed work, and remains available to the homeowner." },
    { h: "Honest System Comparison", p: "Destination Roofing explains when exposed-fastener metal is the practical choice and when standing seam may justify the additional cost." },
    { h: "Repair Before Replacement", p: "A leaking metal roof may qualify for repair or a complete re-screw when the panels remain sound and corrosion is not widespread." },
    { h: "Competitive Pricing", p: "Lean overhead helps Destination Roofing offer competitive pricing without cutting important roofing-system components from the proposal." },
  ];
  return (
    <div className="not-prose grid sm:grid-cols-2 gap-4 my-6">
      {cards.map((c) => (
        <div key={c.h} className="border border-border rounded-lg p-5 bg-card">
          <h3 className="font-display font-bold text-lg mb-2">{c.h}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{c.p}</p>
        </div>
      ))}
    </div>
  );
}

function ComparisonTable() {
  const rows: [string, string, string][] = [
    ["Initial cost", "Lower", "Substantially higher"],
    ["Fastener visibility", "Visible screws through the panel", "Concealed under the seam"],
    ["Maintenance considerations", "Periodic screw, washer, and sealant inspection", "Fewer exposed fasteners; details still require inspection"],
    ["Appearance", "Traditional ribbed metal look", "Cleaner, premium seam profile"],
    ["Panel attachment", "Screws directly through panel into substrate", "Snap-lock with screw strip, snap-lock with clips, or mechanically seamed"],
    ["Typical homeowner", "Wants a metal roof at a reasonable price", "Wants a premium concealed-fastener upgrade"],
    ["Roof complexity", "Works well on most roof designs", "Complex geometry can materially affect price"],
    ["Upgrade level", "Practical metal system", "Premium metal system"],
  ];
  return (
    <div className="not-prose overflow-x-auto my-6 border border-border rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left px-3 py-2 font-semibold">Consideration</th>
            <th className="text-left px-3 py-2 font-semibold">Exposed Fastener</th>
            <th className="text-left px-3 py-2 font-semibold">Standing Seam</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([a, b, c]) => (
            <tr key={a} className="border-t border-border align-top">
              <td className="px-3 py-2 font-medium">{a}</td>
              <td className="px-3 py-2">{b}</td>
              <td className="px-3 py-2">{c}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ThreePathColumns() {
  const cols = [
    {
      h: "Limited repair may make sense when:",
      items: [
        "The leak is isolated",
        "Only a few fasteners or details failed",
        "Panels remain sound",
        "Rust is limited",
        "The problem can be clearly identified",
      ],
      accent: false,
    },
    {
      h: "A complete re-screw may make sense when:",
      items: [
        "Fastener problems are widespread",
        "Panels remain structurally serviceable",
        "Existing holes can accept wider-diameter replacement screws",
        "Sealant and penetration maintenance are also needed",
        "Corrosion is not widespread",
      ],
      accent: true,
    },
    {
      h: "Replacement may make more sense when:",
      items: [
        "Panels are widely rusted",
        "Metal is severely deteriorated",
        "Fasteners no longer hold reliably",
        "Leaks occur across multiple areas",
        "Previous repairs repeatedly fail",
        "The underlying roof system needs broader correction",
        "The owner wants to upgrade to a different metal system",
      ],
      accent: false,
    },
  ];
  return (
    <div className="not-prose grid md:grid-cols-3 gap-4 my-6">
      {cols.map((c) => (
        <div key={c.h} className={`rounded-lg p-5 bg-card ${c.accent ? "border-2 border-cta" : "border border-border"}`}>
          <h3 className="font-display font-bold text-base mb-3">{c.h}</h3>
          <ul className="space-y-1.5 text-sm">
            {c.items.map((i) => (<li key={i}>· {i}</li>))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Services", href: "/services" }, { label: "Metal Roofing", href: "/metal-roofing" }]} />
      <PageHero showRating
        eyebrow="Metal Roofing"
        title="Metal Roofing in Citrus County, FL"
        subtitle="Destination Roofing Inc. installs, replaces, repairs, and re-screws metal roofs throughout Citrus County. We work with exposed-fastener and concealed-fastener systems and explain the real differences in price, appearance, maintenance, and installation before asking a homeowner to choose. Owner and licensed roofing contractor Deston Judy generally performs the inspection personally and remains involved throughout the project."
      />
      <ServicePageBody title="Metal Roofing" service="Metal Roofing">
        <TrustRow />

        <h2>A More Personal Metal-Roofing Experience</h2>
        <WhyCards />

        <h2>Metal-Roofing Systems We Install</h2>
        <p>Destination Roofing works with a range of metal-roofing systems for Florida homes and buildings.</p>
        <h3>Exposed-fastener systems</h3>
        <ul>
          <li>Tough Rib</li>
          <li>Ultra Rib</li>
          <li>5V Crimp</li>
          <li>PBR panels where appropriate</li>
        </ul>
        <h3>Concealed-fastener systems</h3>
        <ul>
          <li>Snap-lock standing seam with screw strip</li>
          <li>Snap-lock standing seam with clips</li>
          <li>Mechanically seamed standing seam</li>
        </ul>
        <p>The right system depends on roof design, roof pitch, budget, desired appearance, building type, panel length, engineering requirements, long-term maintenance expectations, and any HOA or architectural requirements.</p>

        <h2>Exposed-Fastener Metal Roofing</h2>
        <p>
          Exposed-fastener roofing is the most common metal system selected by Destination Roofing customers because it provides the appearance and durability of metal at a substantially lower initial cost than standing seam.
        </p>
        <ul>
          <li>Fasteners pass through the panel and attach it to the roof structure.</li>
          <li>Proper screw placement and washer compression matter.</li>
          <li>Screws must not be driven too loose or over-tightened.</li>
          <li>Fasteners and sealants require periodic inspection as the roof ages.</li>
          <li>Long-life ZAC screws are used where appropriate.</li>
          <li>Wood-to-metal or metal-to-metal fasteners are selected according to the roof assembly.</li>
          <li>Manufacturer-compatible, color-matched sealant is used at appropriate details.</li>
        </ul>
        <p>
          <strong>Gauge:</strong> For residential exposed-fastener metal roofing, Destination Roofing generally uses 26-gauge metal or heavier, depending on the approved panel system, design, and project requirements.
        </p>
        <p>
          <strong>Most common panel:</strong> Tough Rib or Ultra Rib is our most commonly installed exposed-fastener profile. If you would like to see it on your own roof, <Link to="/contact">request a free inspection</Link> or view recent local metal projects below.
        </p>

        <h2>Standing-Seam Metal Roofing</h2>
        <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose">
          <ResponsivePicture
            image={TAN_STANDING_SEAM_PINE_RIDGE}
            alt="Tan standing-seam metal roof installed on a new-construction home in Pine Ridge, Florida"
            sizes="(min-width: 1024px) 66vw, 100vw"
            imgClassName="w-full h-auto"
          />
          <figcaption className="p-3 text-sm text-muted-foreground">New-construction standing-seam metal roof in <Link to="/roofing-company-pine-ridge-fl">Pine Ridge, FL</Link>. <Link to="/projects/standing-seam-metal-roof-pine-ridge-fl">View the Pine Ridge project</Link>.</figcaption>
        </figure>
        <p>Standing seam is a premium concealed-fastener system. The primary panel fasteners are hidden beneath the seams rather than exposed directly to weather.</p>
        <p>Available systems may include:</p>
        <ul>
          <li>Snap-lock panels with a screw strip</li>
          <li>Snap-lock panels installed with clips</li>
          <li>Mechanically seamed panels</li>
        </ul>
        <p>Destination Roofing's most common standing-seam configuration is snap lock with a concealed screw strip, depending on the roof design and manufacturer requirements.</p>
        <p><strong>Gauge:</strong> Standing-seam residential systems are generally installed using approximately 24-gauge metal, depending on the project, engineering, panel system, and manufacturer.</p>
        <h3>Benefits</h3>
        <ul>
          <li>Concealed primary fasteners</li>
          <li>Cleaner appearance</li>
          <li>Premium system design</li>
          <li>Fewer exposed fastener washers requiring long-term maintenance</li>
          <li>Multiple panel and seam options</li>
        </ul>
        <h3>Tradeoffs</h3>
        <ul>
          <li>Significantly higher initial cost</li>
          <li>More detailed fabrication and installation</li>
          <li>Roof geometry can materially affect price</li>
          <li>Not every homeowner needs the premium system</li>
        </ul>

        <h2>Exposed Fastener or Standing Seam: Which Is Better for Your Home?</h2>
        <ComparisonTable />
        <p>
          Exposed-fastener metal is often the practical choice when a homeowner wants a metal roof without the premium price of standing seam. Standing seam may be the better fit when concealed fasteners, appearance, and a premium roof system are higher priorities. Destination Roofing explains both options rather than assuming the highest-priced system is automatically best.
        </p>

        <h2>How Destination Roofing Installs Metal Roofs</h2>
        <ul>
          <li>Existing roofing conditions are inspected before installation.</li>
          <li>High-temperature peel-and-stick underlayment is installed beneath metal roofing.</li>
          <li>Panel type and gauge are selected according to the roof and approved system.</li>
          <li>Panels are measured and installed to maintain straight, consistent lines.</li>
          <li>Fasteners are selected for the actual substrate.</li>
          <li>Exposed screws are checked for proper placement and compression.</li>
          <li>Manufacturer-compatible sealant is used at appropriate details.</li>
          <li>New metal trim and flashing are integrated with the panel system.</li>
          <li>Penetrations, transitions, eaves, gables, valleys, ridges, and walls are detailed according to the roof design.</li>
          <li>Deston personally checks overall workmanship and confirms that fasteners and important details were not missed.</li>
          <li>Final photographs are taken.</li>
        </ul>

        <h2>The Details Beneath and Around a Metal Roof</h2>
        <h3>High-temperature peel-and-stick underlayment</h3>
        <p>Destination Roofing installs high-temperature peel-and-stick underlayment beneath metal roofing. The exact brand may vary based on availability, compatibility, and project requirements.</p>
        <h3>Flashing and trim</h3>
        <p>New metal trim and flashing are installed as integrated parts of the metal-roofing system, including:</p>
        <ul>
          <li>Eaves</li>
          <li>Gables</li>
          <li>Ridges</li>
          <li>Hips</li>
          <li>Valleys</li>
          <li>Sidewalls</li>
          <li>Headwalls</li>
          <li>Roof transitions</li>
        </ul>
        <h3>Pipe and roof penetrations</h3>
        <p>Penetrations require compatible boots, flashing, and sealant. Electrical risers may use neoprene zipper boots where appropriate. Larger chimneys may require a cricket to divert water; the roof and applicable requirements must be evaluated before deciding.</p>

        <h2>Metal-Roof Repair</h2>
        <p>Common problems Destination Roofing repairs on existing metal roofs include:</p>
        <ul>
          <li>Backing-out screws</li>
          <li>Failed rubber washers</li>
          <li>Loose fasteners</li>
          <li>Failed sealant</li>
          <li>Leaks around penetrations</li>
          <li>Damaged trim</li>
          <li>Ridge or transition leaks</li>
          <li>Limited panel damage</li>
          <li>Improper previous repairs</li>
          <li>Rust beginning in isolated locations</li>
        </ul>
        <p>A limited problem may be repairable when the surrounding panels remain structurally sound. Learn more on our <Link to="/roof-repair">roof-repair page</Link>.</p>

        <h2>Complete Metal-Roof Re-Screw Service</h2>
        <p>
          A leaking exposed-fastener metal roof does not always require full replacement. When the panels remain structurally sound and widespread rust is not present, a complete re-screw may restore the fastening system and extend the roof's useful service.
        </p>
        <ol>
          <li>Inspect panel and fastener condition.</li>
          <li>Identify corrosion, panel damage, and failed details.</li>
          <li>Remove the existing screws.</li>
          <li>Install wider-diameter long-life ZAC screws so they can properly bite into the existing fastener openings.</li>
          <li>Use wood-to-metal or metal-to-metal fasteners as appropriate.</li>
          <li>Reseal penetrations and vulnerable metal details.</li>
          <li>Inspect the roof to confirm fasteners were installed and no major details were missed.</li>
          <li>Take final photographs.</li>
        </ol>
        <p>Destination Roofing does not recommend a re-screw when panels are widely rusted, severely deteriorated, damaged beyond repair, or unable to reliably hold new fasteners.</p>

        <h2>Does Your Metal Roof Need Repair, Re-Screwing, or Replacement?</h2>
        <ThreePathColumns />
        <p>Destination Roofing has talked homeowners out of full metal-roof replacement when a complete re-screw was the more reasonable option. When broader deterioration is present, see <Link to="/roof-replacement">roof replacement</Link>.</p>

        <h2>Can a Metal Roof Be Installed Over Existing Shingles?</h2>
        <p>
          Whether metal can be installed over an existing roof depends on the existing layers, roof deck, applicable code, manufacturer requirements, roof design, ventilation, moisture conditions, and approved installation method.
        </p>
        <p>Tear-off offers several advantages:</p>
        <ul>
          <li>Exposes the roof deck</li>
          <li>Allows damaged decking to be identified</li>
          <li>Removes hidden deterioration</li>
          <li>Allows the new underlayment and roof system to begin from a known surface</li>
        </ul>
        <p>Installation over an existing roof should not be recommended automatically simply because it reduces tear-off labor.</p>

        <h2>What Affects Metal-Roofing Cost in Citrus County?</h2>
        <ul>
          <li>Total roof surface area</li>
          <li>Exposed fastener versus standing seam</li>
          <li>Panel profile</li>
          <li>Metal gauge</li>
          <li>Roof pitch</li>
          <li>Number of hips, valleys, ridges, and transitions</li>
          <li>Roof height and access</li>
          <li>Panel lengths</li>
          <li>Existing roofing layers</li>
          <li>Tear-off requirements</li>
          <li>Damaged decking</li>
          <li>Chimneys and crickets</li>
          <li>Skylights</li>
          <li>Electrical risers</li>
          <li>Ventilation</li>
          <li>Complex flashing</li>
          <li>Attached lanais or additions</li>
          <li>Material color and finish</li>
          <li>Engineering or project requirements</li>
          <li>Delivery and fabrication requirements</li>
        </ul>
        <p>Exposed-fastener metal is substantially less expensive than standing seam. Destination Roofing explains both systems and provides a written estimate based on the actual roof rather than publishing a misleading one-price-fits-all number.</p>

        <h2>Our Metal-Roofing Process</h2>
        <ol>
          <li><strong>Initial contact.</strong> Liz or Deston receives the request and creates a work order in AccuLynx.</li>
          <li><strong>Free inspection.</strong> Deston generally inspects the roof personally.</li>
          <li><strong>System recommendation.</strong> The homeowner receives an explanation of exposed fastener, standing seam, repair, re-screw, or replacement options.</li>
          <li><strong>Written estimate.</strong> The estimate is commonly prepared and discussed at the property.</li>
          <li><strong>Material and color selection.</strong> The homeowner selects the approved panel profile, gauge, color, and optional upgrades.</li>
          <li><strong>Permits, tear-off, and installation.</strong> The office handles permit and inspection scheduling. The existing roof is prepared according to the approved scope, and the metal system is installed.</li>
          <li><strong>Owner review and cleanup.</strong> Deston checks workmanship, fasteners, important details, and cleanup. Final photographs are taken.</li>
        </ol>

        <h2>Protecting Your Property During Metal-Roof Installation</h2>
        <ul>
          <li>Landscaping is protected with tarps where appropriate.</li>
          <li>Tarps or plywood may be used around newer or higher-quality driveways.</li>
          <li>Air-conditioning equipment may be shielded using plywood or another suitable barrier.</li>
          <li>Pool areas affected by debris are cleaned.</li>
          <li>Removed fasteners and metal debris are carefully collected.</li>
          <li>Magnetic nail bars are used repeatedly when appropriate.</li>
          <li>Final cleanup and workmanship are personally reviewed by Deston.</li>
        </ul>

        <h2>Recent Metal-Roofing Projects in Citrus County</h2>
        <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose">
          <ResponsivePicture
            image={HERNANDO_GALVALUME_FINISHED}
            alt="Completed Galvalume metal roof installed by Destination Roofing on a residential home in Hernando, Florida"
            sizes="(min-width: 1024px) 66vw, 100vw"
            imgClassName="w-full h-auto"
          />
          <figcaption className="p-3 text-sm text-muted-foreground"><strong>Completed Galvalume Metal Roof in Hernando</strong> — Completed Galvalume metal roofing system on a residential home in <Link to="/roofing-company-hernando-fl">Hernando, Florida</Link>. <Link to="/projects/galvalume-metal-roof-hernando-fl">See the full Hernando project</Link>.</figcaption>
        </figure>

        <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose relative">
          <ResponsivePicture
            image={HOMOSASSA_BLUE_METAL_IN_PROGRESS}
            alt="Destination Roofing crew installing a blue metal roofing system in progress in Homosassa, Florida"
            sizes="(min-width: 1024px) 66vw, 100vw"
            imgClassName="w-full h-auto"
          />
          <div className="absolute top-3 left-3 bg-cta text-brand text-xs font-bold uppercase tracking-wider rounded px-2 py-1">Installation in Progress</div>
          <figcaption className="p-3 text-sm text-muted-foreground"><strong>Blue Metal Roof Installation in Homosassa</strong> — Destination Roofing crew installing a blue metal roofing system in <Link to="/roofing-company-homosassa-fl">Homosassa, Florida</Link>. Installation in progress. <Link to="/projects/blue-metal-roof-homosassa-fl">Project details</Link>.</figcaption>
        </figure>

        <div className="not-prose my-6">
          <h3 className="font-display font-bold text-lg mb-3">Lecanto Metal Roof Before and After</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <figure className="rounded-xl overflow-hidden border border-border bg-white">
              <ResponsivePicture
                image={LECANTO_METAL_BEFORE}
                alt="Existing deteriorated metal roof before replacement in Lecanto, Florida"
                sizes="(min-width: 768px) 45vw, 100vw"
                imgClassName="w-full h-auto"
              />
              <figcaption className="p-3 text-sm text-muted-foreground"><strong>Before</strong> — existing deteriorated metal roofing.</figcaption>
            </figure>
            <figure className="rounded-xl overflow-hidden border border-border bg-white">
              <ResponsivePicture
                image={LECANTO_METAL_AFTER}
                alt="Completed metal roof replacement section by Destination Roofing in Lecanto, Florida"
                sizes="(min-width: 768px) 45vw, 100vw"
                imgClassName="w-full h-auto"
              />
              <figcaption className="p-3 text-sm text-muted-foreground"><strong>After</strong> — completed replacement section in <Link to="/roofing-company-lecanto-fl">Lecanto, FL</Link>. <Link to="/projects/lecanto-metal-roof-before-after">See the before-and-after project</Link>.</figcaption>
            </figure>
          </div>
        </div>

        <div className="not-prose my-6">
          <h3 className="font-display font-bold text-lg mb-3">Standing Seam Galvalume Roof in Lecanto, FL</h3>
          <figure className="rounded-xl overflow-hidden border border-border bg-white">
            <ResponsivePicture
              image={LECANTO_STANDING_SEAM_GALVALUME}
              alt="26-gauge Galvalume standing seam metal roof installed by Destination Roofing in Lecanto, Florida"
              sizes="(min-width: 1024px) 66vw, 100vw"
              imgClassName="w-full h-auto"
            />
            <figcaption className="p-3 text-sm text-muted-foreground">
              26-gauge Galvalume standing seam metal roofing installed by Destination Roofing in <Link to="/roofing-company-lecanto-fl">Lecanto</Link>, Citrus County, Florida. The concealed-fastener standing seam system provides a clean finished appearance while keeping the primary roof fasteners protected beneath the seams.{" "}<Link to="/projects/standing-seam-metal-roof-lecanto-fl">View this standing-seam project</Link>.
            </figcaption>
          </figure>
        </div>

        <p><Link to="/projects">View all roofing projects</Link> · <Link to="/gallery">Photo gallery</Link></p>

        <h2>Metal-Roof Workmanship Warranty</h2>
        <p>Standard full-roof installations generally receive a 15-year Destination Roofing workmanship warranty. The workmanship warranty generally covers the labor and material required to correct leaks caused by Destination Roofing's improper installation, subject to the written contract, exclusions, and warranty terms.</p>
        <p>Repair and re-screw warranty terms follow the written proposal and warranty for that specific scope of work.</p>
        <p>If a legitimate installation-related problem develops, Destination Roofing's approach is to inspect the issue and honor the written warranty rather than automatically search for an excuse to avoid responsibility.</p>

        <h2>Metal Roofing for Citrus County Weather</h2>
        <ul>
          <li>Florida sun and heat place demands on coatings, sealants, and underlayment.</li>
          <li>Heavy rain tests valleys, walls, penetrations, and roof transitions.</li>
          <li>High winds place stress on panel edges, fasteners, seams, and trim.</li>
          <li>Exposed-fastener roofs require long-term attention to washers and screws.</li>
          <li>Standing-seam systems conceal the main panel fasteners but still require proper flashing and detailing.</li>
          <li>Humidity and trapped moisture make correct underlayment and ventilation important.</li>
          <li>Low-slope additions require systems and drainage appropriate for the roof pitch — see <Link to="/flat-roofing">flat roofing</Link>.</li>
          <li>Roof-to-wall connections can be evaluated for <Link to="/third-nail-wind-mitigation">third-nail wind-mitigation improvements</Link> during a larger project.</li>
        </ul>
        <p>Related resources: <Link to="/soffit">soffit and fascia</Link>, <Link to="/storm-damage-roof-repair">storm-damage roof repair</Link>, <Link to="/commercial-roofing">commercial roofing</Link>, and <Link to="/shingle-roofing">shingle roofing</Link>.</p>
        <p>For manufactured homes, learn about our <Link to="/mobile-home-roofing">mobile home metal roof-over and repair options</Link>.</p>
        <p>Compare our <Link to="/roof-cost-citrus-county">2026 metal and shingle roof-cost ranges</Link> for Citrus County.</p>

        <h2>What Homeowners Say About Destination Roofing</h2>
        <div className="not-prose grid gap-4 md:grid-cols-3 my-6">
          {REVIEWS.map((r) => (
            <blockquote key={r.name} className="border border-border rounded-lg p-5 bg-card">
              <p className="text-sm leading-relaxed">"{r.text}"</p>
              <footer className="mt-3 text-sm font-semibold">— {r.name}</footer>
            </blockquote>
          ))}
        </div>
        <p><Link to="/reviews">Read More Customer Reviews</Link></p>

        <h2>Metal-Roofing Guidance From an Experienced Local Roofer</h2>
        <p>This page is reviewed by Deston Judy, owner and licensed roofing contractor for Destination Roofing Inc. Deston began working in roofing at age 15 and personally performs many inspections, explains metal-roofing options, reviews estimates, and checks completed work.</p>
        <ul>
          <li>Florida roofing license #{" "}{BUSINESS.licenseNumber}</li>
          <li>More than 100 roofs completed by Destination Roofing</li>
          <li>Approximately 15 years rooted in Citrus County</li>
        </ul>
        <p><Link to="/about">More about Deston and Destination Roofing →</Link></p>

        <h2>Metal-Roofing Questions</h2>
        <div className="not-prose space-y-3 my-6">
          {FAQS.map((f) => (
            <details key={f.q} className="border border-border rounded-lg bg-card">
              <summary className="cursor-pointer font-semibold px-4 py-3">{f.q}</summary>
              <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>

        <h2>Metal Roofing Throughout Citrus County</h2>
        <p>
          We install and service metal roofs across Citrus County, including <Link to="/roofing-company-beverly-hills-fl">Beverly Hills</Link>, <Link to="/roofing-company-inverness-fl">Inverness</Link>, <Link to="/roofing-company-crystal-river-fl">Crystal River</Link>, <Link to="/roofing-company-homosassa-fl">Homosassa</Link>, <Link to="/roofing-company-lecanto-fl">Lecanto</Link>, <Link to="/roofing-company-hernando-fl">Hernando</Link>, <Link to="/roofing-company-floral-city-fl">Floral City</Link>, <Link to="/roofing-company-citrus-springs-fl">Citrus Springs</Link>, <Link to="/roofing-company-dunnellon-fl">Dunnellon</Link>, and <Link to="/roofing-company-spring-hill-fl">Spring Hill</Link>. Browse all <Link to="/service-areas">service areas</Link> and <Link to="/services">roofing services</Link>, or <Link to="/roof-inspection">request a free roof inspection</Link>.
        </p>

        <h2 id="request-inspection">Get an Honest Metal-Roofing Recommendation</h2>
        <p>Destination Roofing will inspect the actual roof and explain whether repair, re-screwing, exposed-fastener replacement, or standing seam makes the most sense.</p>
        <div className="not-prose flex flex-col sm:flex-row gap-3 my-4">
          <Link to="/contact" className="btn-cta w-full sm:w-auto">
            <CalendarCheck className="size-5" /> Schedule My Free Inspection
          </Link>
          <a href={BUSINESS.phoneHref} className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-display font-bold uppercase tracking-wider rounded-md px-6 py-3 w-full sm:w-auto hover:bg-brand hover:text-brand-foreground transition-colors">
            <Phone className="size-5" /> Call {BUSINESS.phone}
          </a>
        </div>
      </ServicePageBody>
    </>
  );
}
